import { type ReactNode } from 'react'

import { cn } from './cn'

export interface EmptyStateProps {
  title: ReactNode
  description?: ReactNode
  /** The phase that fills this screen, e.g. `P3`. Rendered as a chip. */
  phase?: string
  children?: ReactNode
  className?: string
}

/**
 * "Nothing is here yet, and here is why."
 *
 * The five product screens outside Admin are unbuilt at P0. They render this
 * rather than a plausible-looking screen: the hatch fill is the mockup's own
 * placeholder texture (`.gw-ph`), and naming the phase means nobody has to
 * open the plan to find out whether a blank Queue is a bug or a schedule.
 */
export function EmptyState({ title, description, phase, children, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed border-divider bg-surface px-6 py-14 text-center',
        className,
      )}
    >
      <div aria-hidden="true" className="gw-ph h-10 w-24 rounded-md" />
      <div className="flex flex-col items-center gap-1.5">
        <h4 className="m-0">{title}</h4>
        {description ? (
          <p className="m-0 max-w-[520px] text-body text-muted">{description}</p>
        ) : null}
      </div>
      {phase ? (
        <span className="tag tag-outline font-mono">lands in {phase}</span>
      ) : null}
      {children}
    </div>
  )
}
